/**
 * Cross-origin iframe communication utilities
 * Supports same-origin and cross-origin iframe embedding scenarios
 */
import { MODAL_CHECKOUT_MESSAGE_HOOK_MAP } from '@/utils/modal-utils';
import { generateOpcUniqueId } from '@/utils/index';

// Cache for cross-origin detection result
let _isCrossOriginCache = null;

/**
 * Detect if current context is cross-origin
 * @returns {boolean} true if cross-origin, false if same-origin
 */
export function isCrossOrigin() {
  // Return cached result if available
  if (_isCrossOriginCache !== null) {
    return _isCrossOriginCache;
  }

  try {
    void window.parent.location.href;
    _isCrossOriginCache = false; // Same-origin
    return false;
  } catch (e) {
    _isCrossOriginCache = true; // Cross-origin
    return true;
  }
}


/**
 * Cross-origin communication bridge
 * Provides unified cross-origin communication interface
 */
export const CrossOriginBridge = {
  /**
   * Send message to parent window
   * @param {string} type Message type
   * @param {any} data Message data
   * @param {string} targetOrigin Target origin, defaults to '*'
   */
  send(type, data, targetOrigin = '*') {
    if (window.parent) {
      window.parent.postMessage({ type, ...data }, targetOrigin);
    }
  },

  /**
   * Listen for messages from parent window
   * @param {string} type Message type
   * @param {Function} handler Handler function
   */
  on(type, handler) {
    window.addEventListener('message', (e) => {
      if (e?.data?.type === type) {
        handler(e.data, e);
      }
    });
  },
};

/**
 * Safe parent window function call
 * Works in both same-origin and cross-origin scenarios
 * @param {string} objectPath Object path (e.g., 'zoomCampaignSdk.updateUserContext')
 * @param {...any} args Function arguments
 */
export function safeParentCall(objectPath, ...args) {
  if (!shouldUseCrossOrigin()) {
    // Same-origin or non-embedded mode, use original logic
    const pathParts = objectPath.split('.');
    const lastProp = pathParts.pop();
    const obj = pathParts.reduce((current, prop) => current?.[prop], window.top);
    const func = obj?.[lastProp];

    if (typeof func === 'function') {
      // Use .call() to maintain proper 'this' context
      return func.call(obj, ...args);
    }
  } else {
    // Cross-origin mode, use safe communication
      CrossOriginBridge.send(MODAL_CHECKOUT_MESSAGE_HOOK_MAP.safeCall, {
      objectPath,
      args
    });
  }
}

/**
 * Safe parent window property get
 * Works in both same-origin and cross-origin scenarios
 * @param {string} objectPath Object path (e.g., 'zoomCampaignSdk.isInit')
 * @returns {Promise<any>} Property value (Promise for cross-origin, resolved Promise for same-origin)
 */
export function safeParentGet(objectPath) {
  if (!shouldUseCrossOrigin()) {
    // Same-origin or non-embedded mode, use original logic
    return Promise.resolve(objectPath.split('.').reduce((current, prop) => current?.[prop], window.top));
  } else {
    // Cross-origin mode, use safe communication with Promise
    return new Promise((resolve, reject) => {
      const requestId = generateOpcUniqueId();

      // Set up response listener
      const handleResponse = (event) => {
        if (event.data?.type === MODAL_CHECKOUT_MESSAGE_HOOK_MAP.safeGetResponse &&
            event.data?.requestId === requestId) {
          window.removeEventListener('message', handleResponse);
          if (event.data.error) {
            reject(new Error(event.data.error));
          } else {
            resolve(event.data.value);
          }
        }
      };

      // Add timeout
      const timeout = setTimeout(() => {
        window.removeEventListener('message', handleResponse);
        reject(new Error('Cross-origin property access timeout'));
      }, 2000); // 2 second timeout

      window.addEventListener('message', handleResponse);

      // Send request
      CrossOriginBridge.send(MODAL_CHECKOUT_MESSAGE_HOOK_MAP.safeGetCall, {
        objectPath,
        requestId
      });

      // Clear timeout on resolve/reject
      const originalResolve = resolve;
      const originalReject = reject;
      resolve = (value) => {
        clearTimeout(timeout);
        originalResolve(value);
      };
      reject = (error) => {
        clearTimeout(timeout);
        originalReject(error);
      };
    });
  }
}

/**
 * Safe parent window property set
 * Works in both same-origin and cross-origin scenarios
 * @param {string} objectPath Object path (e.g., 'OPC_INFO_FOR_ZVA')
 * @param {any} value Value to set
 */
export function safeParentSet(objectPath, value) {
  if (!shouldUseCrossOrigin()) {
    // Same-origin or non-embedded mode, use original logic
    const pathParts = objectPath.split('.');
    const lastProp = pathParts.pop();
    const obj = pathParts.reduce((current, prop) => current?.[prop], window.top);
    if (obj) {
      obj[lastProp] = value;
    }
  } else {
    // Cross-origin mode, use safe communication
      CrossOriginBridge.send(MODAL_CHECKOUT_MESSAGE_HOOK_MAP.safeSet, {
      objectPath,
      value
    });
  }
}

/**
 * Check if should use cross-origin communication
 * @returns {boolean} true if should use cross-origin communication
 */
export function shouldUseCrossOrigin() {
  return !window.campaignFlags?.forceSameOriginSwitch && window.$context?.is?.modal && isCrossOrigin();
}
